import React, { useState } from "react";
import {
	Box,
	Typography,
	Paper,
	TextField,
	Switch,
	Button,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	IconButton,
	useMediaQuery,
	Snackbar,
	Alert,
} from "@mui/material";
import { Delete, Devices } from "@mui/icons-material";

const AccountSecurityDashboard = () => {
	// Password change states
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [passwordSnackbar, setPasswordSnackbar] = useState(false);

	// 2FA toggle state
	const [is2FAEnabled, setIs2FAEnabled] = useState(false);
	const [isProcessing2FA, setIsProcessing2FA] = useState(false);
	const [twoFAActionMsg, setTwoFAActionMsg] = useState("");

	// Devices list
	const [devices, setDevices] = useState([
		{ id: 1, name: "Chrome on Windows", lastActive: "Today" },
		{ id: 2, name: "Safari on iPhone", lastActive: "Yesterday" },
	]);
	const [deviceToRemove, setDeviceToRemove] = useState(null);
	const [deviceActionMsg, setDeviceActionMsg] = useState("");

	const isSmallScreen = useMediaQuery("(max-width:600px)");

	// Handlers
	const handlePasswordChange = async () => {
		// Simulate API call
		await new Promise((res) => setTimeout(res, 1000));
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
		setPasswordSnackbar(true);
	};

	const handleToggle2FA = async () => {
		setIsProcessing2FA(true);
		await new Promise((res) => setTimeout(res, 1000));
		setIs2FAEnabled((prev) => !prev);
		setTwoFAActionMsg(`2FA ${!is2FAEnabled ? "enabled" : "disabled"}`);
		setIsProcessing2FA(false);
	};

	const handleRemoveDevice = () => {
		if (deviceToRemove) {
			setDevices(devices.filter((d) => d.id !== deviceToRemove.id));
			setDeviceToRemove(null);
			setDeviceActionMsg("Device removed successfully");
		}
	};

	return (
		<Box
			sx={{
				maxWidth: 900,
				mx: "auto",
				p: 3,
				bgcolor: "#fafafa",
				mt: "1.5rem",
			}}>
			{/* Header */}
			<Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
				Account Security Settings
			</Typography>

			{/* Change Password */}
			<Paper sx={{ p: 3, mb: 4, boxShadow: 2 }}>
				<Typography variant="h6" gutterBottom>
					Change Password
				</Typography>
				<Box
					sx={{
						display: "flex",
						flexDirection: isSmallScreen ? "column" : "row",
						gap: 2,
						alignItems: "center",
					}}>
					<TextField
						label="Current Password"
						type="password"
						fullWidth
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
					/>
					<TextField
						label="New Password"
						type="password"
						fullWidth
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
					/>
					<TextField
						label="Confirm New Password"
						type="password"
						fullWidth
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
					/>
				</Box>
				<Button
					variant="contained"
					color="primary"
					sx={{ mt: 2 }}
					onClick={handlePasswordChange}>
					Update Password
				</Button>
			</Paper>

			{/* 2FA Toggle */}
			<Paper sx={{ p: 3, mb: 4, boxShadow: 2 }}>
				<Typography variant="h6" gutterBottom>
					Two-Factor Authentication (2FA)
				</Typography>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}>
					<Typography>{is2FAEnabled ? "Enabled" : "Disabled"}</Typography>
					<Switch
						checked={is2FAEnabled}
						onChange={handleToggle2FA}
						disabled={isProcessing2FA}
						color="primary"
					/>
				</Box>
				{isProcessing2FA && (
					<Typography variant="body2">Processing...</Typography>
				)}
				{twoFAActionMsg && (
					<Typography variant="body2" color="success.main">
						{twoFAActionMsg}
					</Typography>
				)}
			</Paper>

			{/* Manage Devices */}
			<Paper sx={{ p: 3, boxShadow: 2 }}>
				<Typography variant="h6" gutterBottom>
					Manage Login Devices
				</Typography>
				<List>
					{devices.map((device) => (
						<ListItem key={device.id} divider>
							<Devices sx={{ mr: 2 }} />
							<ListItemText
								primary={device.name}
								secondary={`Last active: ${device.lastActive}`}
							/>
							<ListItemSecondaryAction>
								<IconButton
									edge="end"
									aria-label="remove device"
									onClick={() => setDeviceToRemove(device)}>
									<Delete />
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List>

				{/* Confirm removal */}
				{deviceToRemove && (
					<Box
						sx={{
							mt: 2,
							display: "flex",
							flexDirection: "column",
							gap: 2,
							alignItems: "center",
						}}>
						<Typography>
							Are you sure you want to remove{" "}
							<strong>{deviceToRemove.name}</strong>?
						</Typography>
						<Box sx={{ display: "flex", gap: 2 }}>
							<Button
								variant="outlined"
								color="error"
								onClick={() => setDeviceToRemove(null)}>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="error"
								onClick={handleRemoveDevice}>
								Remove
							</Button>
						</Box>
					</Box>
				)}
				{/* Optional: Add "Activate" button or toggle here if needed */}
			</Paper>

			{/* Snackbar for feedback */}
			<Snackbar
				open={passwordSnackbar}
				autoHideDuration={3000}
				onClose={() => setPasswordSnackbar(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					severity="success"
					onClose={() => setPasswordSnackbar(false)}
					sx={{ width: "100%" }}>
					Password updated successfully!
				</Alert>
			</Snackbar>

			{/* Feedback for device actions */}
			<Snackbar
				open={Boolean(deviceActionMsg)}
				autoHideDuration={3000}
				onClose={() => setDeviceActionMsg("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					severity="success"
					onClose={() => setDeviceActionMsg("")}
					sx={{ width: "100%" }}>
					{deviceActionMsg}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default AccountSecurityDashboard;
